import React, { ChangeEvent, useState, MouseEvent } from 'react'
import InputTextField from '../common/form-elements/InputTextField'
import Link from 'next/link'
import { Eye, EyeOff } from 'react-feather'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { useLocale } from 'src/lib/hooks/useLocal'
import { validateEmail } from 'src/lib/utils/helpers'
import { monthlyOrderValues, roleValues } from 'src/lib/utils/user'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI, constants } from 'src/lib/utils/constants'
import Alerts from '../common/feedback-and-dialog/Alerts'
import { useRouter } from 'next/router'
import { errorToast } from 'src/lib/utils/toasters'
import { regexPatterns } from 'src/lib/utils/validations'

import { UserProfileState, UserProfileErrorState } from 'src/lib/types/settings'
import { defaultUserProfile, defaultUserProfileErrorState } from 'src/assets/SettingsConfig'
import PhoneNumberInputField from '../common/form-elements/PhoneNumberInputField'
import 'react-phone-input-2/lib/style.css'
import useReCaptcha from 'src/lib/hooks/useReCaptcha'
import Cookies from 'js-cookie'
interface PasswordState {
  value: string
  showValue: boolean
}

const SignUp = () => {
  const { t } = useLocale()
  const router = useRouter()
  const { generateReCaptchaToken } = useReCaptcha()

  const [userProfile, setUserProfile] = useState<UserProfileState>(defaultUserProfile)
  const [shopName, setShopName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<PasswordState>({ value: '', showValue: false })
  const [rePassword, setRePassword] = useState<PasswordState>({ value: '', showValue: false })
  const [mov, setMOV] = useState<string>(monthlyOrderValues[0].value)

  const [shopNameError, setShopNameError] = useState<boolean>(false)
  const [emailError, setEmailError] = useState<boolean>(false)
  const [passwordError, setPasswordError] = useState<boolean>(false)
  const [rePasswordError, setRePasswordError] = useState<boolean>(false)
  const [userProfileErrors, setUserProfileErrors] = useState<UserProfileErrorState>(defaultUserProfileErrorState)
  const [credentialsInvalidMessage, setCredentialsInvalidMessage] = useState<string>('')

  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const inputStyle = {
    style: {
      marginLeft: -16,
      marginRight: -16,
      paddingLeft: 16,
      paddingRight: 14,
      width: '104%',
      borderTopRightRadius: '8px',
      borderBottomRightRadius: '8px',
      fontSize: 14
    }
  }
  const dropdownStyle = {
    fontWeight: 400,
    fontSize: 14
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: any, reason: string) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }
  const validateCredentials = async () => {
    if (!Boolean(userProfile?.firstName) || userProfile?.firstName?.match(regexPatterns.alphaNumericExcludesSpace)) {
      setUserProfileErrors({
        ...userProfileErrors,
        firstName: true
      })
      return
    }
    if (
      !Boolean(userProfile?.phoneNumber) ||
      (typeof userProfile?.phoneNumber === 'string' && userProfile?.phoneNumber?.length < 12)
    ) {
      setUserProfileErrors({
        ...userProfileErrors,
        phoneNumber: true
      })
      return
    }
    if (!Boolean(userProfile?.role)) {
      setUserProfileErrors({
        ...userProfileErrors,
        role: true
      })
      return
    }
    if (!validateEmail(email)) {
      setEmailError(true)
      return
    }
    if (!Boolean(shopName)) {
      setShopNameError(true)
      return
    }
    if (!Boolean(password.value)) {
      setPasswordError(true)
      return
    }
    if (!Boolean(rePassword.value)) {
      setRePasswordError(true)
      return
    }

    if (password.value !== rePassword.value) {
      setCredentialsInvalidMessage(t('passwords_not_match'))
      return
    }

    if (!password.value.match(regexPatterns.passwordPattern)) {
      setCredentialsInvalidMessage(t('password_wrong_format'))
      return
    }

    const payload = {
      shop_name: shopName,
      email: email.trim(),
      password: password.value,
      monthly_order_value_band: mov,
      first_name: userProfile?.firstName ?? '',
      last_name: userProfile?.lastName?.toString()?.length ? userProfile?.lastName : 'N/A',
      phone_number: userProfile?.phoneNumber ? '+'.concat(userProfile?.phoneNumber) : '',
      role: userProfile?.role ?? '',
      captcha_token: await generateReCaptchaToken('signup')
    }

    try {
      setIsLoading(true)
      setCredentialsInvalidMessage('')
      await postRequest('/merchant-external-onboarding', apiURI.AUTH_ENGINE, payload)
      setIsLoading(false)
      Cookies.set(constants.FLO_SHOPFLO_VERSION, 'stable')
      router.push({
        pathname: '/auth/email-sent',
        query: { email }
      })
    } catch (error: any) {
      setIsLoading(false)
      const errorCode = error?.response?.data?.error?.code ?? error?.data?.error?.code

      if (errorCode === 'AUTH_009') {
        errorToast('Account already exists! Kindly login to continue', 2500)
        setTimeout(() => {
          router.push('/auth/login')
        }, 2500)
        return
      }

      if (errorCode === 'GE_001') {
        errorToast('Password format is invalid')
        return
      }

      errorToast(t('something_went_wrong'))
    }
  }

  const handlePasswordChange = (type: 'password' | 'repassword') => (event: ChangeEvent<HTMLInputElement>) => {
    switch (type) {
      case 'password':
        if (passwordError) {
          setPasswordError(false)
        }
        setPassword({ ...password, value: event.target.value })
        return
      case 'repassword':
        if (rePasswordError) {
          setRePasswordError(false)
        }
        setRePassword({ ...rePassword, value: event.target.value })
        return
      default:
        return
    }
  }

  const handleClickShowPassword = (type: 'password' | 'repassword') => {
    switch (type) {
      case 'password':
        setPassword({ ...password, showValue: !password.showValue })
        return
      case 'repassword':
        setRePassword({ ...rePassword, showValue: !rePassword.showValue })
        return
      default:
        return
    }
  }

  return (
    <>
      <form
        noValidate
        autoComplete='off'
        onSubmit={e => e.preventDefault()}
        className='flex flex-col border w-[26.5rem] bg-[#FFFFFF] rounded-2xl p-4 gap-4'
      >
        <div className=''>
          <InputTextField
            isRequired={true}
            textFieldId='first-name'
            inputProps={inputStyle}
            inputValue={userProfile?.firstName}
            handleChange={e =>
              setUserProfile({
                ...userProfile,
                firstName: e.target.value
              })
            }
            handleFocus={() => setUserProfileErrors({ ...userProfileErrors, firstName: false })}
            label={t('first_name')}
            inputType='text'
            inputPlaceholder={t('first_name_placeholder')}
            errorMessage={t('valid_first_name')}
            errorStatus={Boolean(userProfileErrors?.firstName)}
          />
        </div>
        <div className=''>
          <InputTextField
            textFieldId='last-name'
            inputProps={inputStyle}
            inputValue={userProfile?.lastName}
            handleChange={e =>
              setUserProfile({
                ...userProfile,
                lastName: e.target.value
              })
            }
            label={t('last_name')}
            inputType='text'
            inputPlaceholder={t('last_name_placeholder')}
          />
        </div>
        <div>
          <PhoneNumberInputField
            handleFocus={() => setUserProfileErrors({ ...userProfileErrors, phoneNumber: false })}
            label={t('phone_number')}
            isRequired={true}
            inputValue={userProfile?.phoneNumber}
            handleChange={e =>
              setUserProfile({
                ...userProfile,
                phoneNumber: e
              })
            }
            errorMessage={t('valid_phone_number')}
            errorStatus={Boolean(userProfileErrors?.phoneNumber)}
            onlyCountries={['in']}
          />
        </div>
        <div className=''>
          <label className='font-medium text-[12px] tracking-[.15px] text-[#6e6e6e] leading-normal'>
            {t('role')}
            <span className='text-[#CB2711]'>*</span>
          </label>
          <Select
            labelId='role-select-label'
            id='role-select'
            value={userProfile?.role}
            onChange={(e: any) => {
              setUserProfile({
                ...userProfile,
                role: e.target.value
              })
            }}
            inputProps={inputStyle}
            className='w-full h-10'
            sx={{ ...dropdownStyle, padding: 0 }}
          >
            {roleValues.map((data: any) => (
              <MenuItem key={data.label} value={data.value} sx={dropdownStyle}>
                {data.label}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className=''>
          <InputTextField
            isRequired={true}
            label='Email'
            inputValue={email}
            inputProps={{
              style: {
                marginLeft: -16,
                marginRight: -16,
                paddingLeft: 14,
                paddingRight: 14,
                width: '104%',
                fontSize: 14
              }
            }}
            textFieldId={t('email')}
            handleChange={e => setEmail(e.target.value)}
            inputType='email'
            inputPlaceholder={t('email_placeholder')}
            errorMessage={t('valid_email')}
            errorStatus={emailError}
          />
        </div>
        <div className=''>
          <InputTextField
            isRequired={true}
            textFieldId='shop-name'
            inputProps={inputStyle}
            inputValue={shopName}
            handleChange={e => setShopName(e.target.value)}
            label={t('shop_name')}
            inputType='text'
            inputPlaceholder={t('shop_name_placeholder')}
            errorMessage={t('valid_shop_name')}
            errorStatus={shopNameError}
            handleFocus={() => setShopNameError(false)}
          />
        </div>
        <div className=''>
          <label className='font-medium text-[12px] tracking-[.15px] text-[#6e6e6e] leading-normal'>
            {t('current_mov')}
          </label>
          <Select
            labelId='mov-select-label'
            id='mov-select'
            value={mov}
            onChange={(e: any) => {
              setMOV(e.target.value)
            }}
            inputProps={inputStyle}
            className='w-full h-10'
            sx={{ ...dropdownStyle, padding: 0 }}
          >
            {monthlyOrderValues.map((data: any) => (
              <MenuItem key={data.label} value={data.value} sx={dropdownStyle}>
                {data.label}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className=''>
          <InputTextField
            isRequired={true}
            label={t('enter_password')}
            inputProps={{
              style: {
                marginLeft: -16,
                marginRight: -8,
                paddingLeft: 16,
                paddingRight: 14,
                width: '104%',
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                fontSize: 14
              }
            }}
            handleFocus={() => {
              setCredentialsInvalidMessage('')
            }}
            textFieldId='password'
            handleChange={handlePasswordChange('password')}
            inputType={password.showValue ? 'text ' : 'password'}
            inputValue={password.value}
            inputPlaceholder={t('password_placeholder')}
            errorStatus={passwordError}
            errorMessage={t('valid_password')}
            endIcon={
              <button
                className={`w-10 h-9 flex justify-center items-center rounded-br-lg rounded-tr-lg
              transition duration-300 ${
                password.showValue ? 'bg-gray-light' : 'bg-gray-lightest'
              } hover:bg-[#e0e0e0] -mr-2`}
                //aria-label='toggle password visibility'
                onClick={() => handleClickShowPassword('password')}
              >
                {!password.showValue ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
              </button>
            }
          />
        </div>
        <div className=''>
          <InputTextField
            isRequired={true}
            label={t('renter_password')}
            inputProps={{
              style: {
                marginLeft: -16,
                marginRight: -8,
                paddingLeft: 16,
                paddingRight: 14,
                width: '104%',
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                fontSize: 14
              }
            }}
            handleFocus={() => {
              setCredentialsInvalidMessage('')
            }}
            textFieldId='repassword'
            inputValue={rePassword.value}
            inputType={rePassword.showValue ? 'text ' : 'password'}
            handleChange={handlePasswordChange('repassword')}
            inputPlaceholder={t('repassword_placeholder')}
            errorStatus={rePasswordError}
            errorMessage={t('valid_password')}
            endIcon={
              <button
                className={`w-10 h-9 flex justify-center items-center rounded-br-lg rounded-tr-lg
              transition duration-300 ${
                rePassword.showValue ? 'bg-[#f0f0f0]' : 'bg-[#fafafa]'
              } hover:bg-[#e0e0e0] -mr-2`}
                // aria-label='toggle repassword visibility'
                onClick={() => handleClickShowPassword('repassword')}
              >
                {!rePassword.showValue ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
              </button>
            }
          />
        </div>
        {Boolean(credentialsInvalidMessage) && (
          <p className='text-sm text-center text-[#f44336]'>{credentialsInvalidMessage}</p>
        )}
        <div className=''>
          <button
            type='submit'
            onClick={validateCredentials}
            disabled={isLoading}
            className='disabled:bg-[#E0E0E0] disabled:cursor-not-allowed w-full h-8 text-sm font-semibold  rounded-lg bg-primary-dark text-[#ffffff] disabled:text-[#949494]'
          >
            {t('sign_up')}
          </button>
        </div>
        <div className='text-sm text-center'>
          <p className='text-carbon'>
            {t('already_have_an_account')}&nbsp;
            <span className='font-semibold text-coal-dark'>
              <Link href='/auth/login'>{t('click_to_login')}</Link>
            </span>
          </p>
        </div>
      </form>
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}

export default SignUp
